/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { SearchIcon } from "lucide-react"
import { observer } from "mobx-react-lite"

import { UserListViewModel } from "@/features/user/user-list-viewmodel"
import { formatRole, UserRole } from "@/features/user/user-types"
import { useViewModel } from "../app/app-hooks"
import { InputGroup, InputGroupAddon, InputGroupInput } from "../primitives/input-group"
import { NativeSelect, NativeSelectOption } from "../primitives/native-select"

/**
 * Filters the user list by name or email, status, type and role.
 *
 * Reads and writes the filter on `UserListViewModel`.
 */
export const UserFilters = observer(() => {
    const listVM = useViewModel(UserListViewModel)

    return (
        <fieldset className="m-0 mb-6 grid gap-2 p-0">
            <legend className="m-0 text-sm font-bold uppercase">Filter</legend>

            <div className="flex flex-wrap gap-2">
                <InputGroup className="w-64 bg-background">
                    <InputGroupInput
                        aria-label="Search users"
                        placeholder="Name or email"
                        value={listVM.filter.search}
                        onChange={(e) => listVM.setSearchFilter(e.target.value)}
                    />
                    <InputGroupAddon>
                        <SearchIcon />
                    </InputGroupAddon>
                </InputGroup>

                <NativeSelect
                    className="bg-background"
                    aria-label="Status"
                    value={toOptionValue(listVM.filter.status)}
                    onChange={(e) => listVM.setStatusFilter(fromOptionValue(e.target.value))}
                >
                    <NativeSelectOption value="">Any status</NativeSelectOption>
                    <NativeSelectOption value="true">Enabled</NativeSelectOption>
                    <NativeSelectOption value="false">Disabled</NativeSelectOption>
                </NativeSelect>

                <NativeSelect
                    className="bg-background"
                    aria-label="Type"
                    value={toOptionValue(listVM.filter.external)}
                    onChange={(e) => listVM.setExternalFilter(fromOptionValue(e.target.value))}
                >
                    <NativeSelectOption value="">Internal and external</NativeSelectOption>
                    <NativeSelectOption value="false">Internal</NativeSelectOption>
                    <NativeSelectOption value="true">External</NativeSelectOption>
                </NativeSelect>

                <NativeSelect
                    className="bg-background"
                    aria-label="Role"
                    value={listVM.filter.role ?? ""}
                    onChange={(e) =>
                        listVM.setRoleFilter(Object.values(UserRole).find((role) => role === e.target.value) ?? null)
                    }
                >
                    <NativeSelectOption value="">All roles</NativeSelectOption>
                    {Object.values(UserRole).map((role) => (
                        <NativeSelectOption key={role} value={role}>
                            {formatRole(role)}
                        </NativeSelectOption>
                    ))}
                </NativeSelect>
            </div>
        </fieldset>
    )
})

/**
 * Encodes a yes/no filter as an option value; `""` is the option for no filter.
 */
function toOptionValue(value: boolean | null): string {
    return value === null ? "" : String(value)
}

/**
 * Decodes an option value written by `toOptionValue`.
 */
function fromOptionValue(value: string): boolean | null {
    return value === "" ? null : value === "true"
}
